import { LoaderFunction, LoaderFunctionArgs } from 'react-router-dom'
import { z } from 'zod'

import type { Location } from '@/apiTypes.ts'
import { fetchFromApi } from '@/utils/fetchFromApi.ts'

interface LoaderData {
  locations?: Location[]
  error?: string
  postalCode?: string
  postalCodeInvalid?: boolean
  loadTime?: number
}

const postalCodeSchema = z
  .string()
  .regex(/^[1-9][0-9]{3}\s?[A-Z]{2}$/i, 'Please enter a valid postal code')
  .optional()

const loader: LoaderFunction = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<LoaderData | Response | null> => {
  const { appointmentType } = params
  const url = new URL(request.url)
  const postalCode = url.searchParams.get('postalCode') ?? undefined

  let postalCodeInvalid = false
  if (postalCode) {
    const result = postalCodeSchema.safeParse(postalCode)
    postalCodeInvalid = !result.success
  }

  const startTime = Date.now()
  try {
    const apiUrl = `locations?type=${appointmentType}${
      postalCode && !postalCodeInvalid ? `&postalCode=${postalCode}` : ''
    }`
    const response = await fetchFromApi(apiUrl)

    if (!response.ok) {
      throw new Error('Failed to fetch locations')
    }

    const locations: Location[] = await response.json()
    return {
      loadTime: Date.now() - startTime,
      locations,
      postalCode,
      postalCodeInvalid,
    }
  } catch (error) {
    console.error('Error fetching locations:', error)
    return {
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
      locations: [],
      postalCode,
      postalCodeInvalid,
    }
  }
}

export type { LoaderData }
export { loader }
